import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  Warehouse,
  CreateWarehouseInput,
  UpdateWarehouseInput,
} from "../types";

const WAREHOUSE_FIELDS = `
  id
  organizationId
  name
  code
  type
  addressLine1
  addressLine2
  city
  state
  country
  postalCode
  contactPerson
  phone
  email
  capacity
  isActive
  status
  createdAt
  updatedAt
`;

export const warehouseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // organizationId optional — resolver falls back to the caller's org.
    getWarehouses: builder.query<Warehouse[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Warehouses($organizationId: String) {
              warehouses(organizationId: $organizationId) {
                ${WAREHOUSE_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Warehouse[]>(res, "warehouses"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((w) => ({ type: "Warehouse" as const, id: w.id })),
              { type: "Warehouse" as const, id: "LIST" },
            ]
          : [{ type: "Warehouse" as const, id: "LIST" }],
    }),

    getWarehouse: builder.query<Warehouse, string>({
      query: (id) => ({
        body: {
          query: `
            query Warehouse($id: String!) {
              warehouse(id: $id) {
                ${WAREHOUSE_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Warehouse>(res, "warehouse"),

      providesTags: (_result, _err, id) => [{ type: "Warehouse", id }],
    }),

    createWarehouse: builder.mutation<Warehouse, CreateWarehouseInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateWarehouse($input: CreateWarehouseInput!) {
              createWarehouse(input: $input) {
                ${WAREHOUSE_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<Warehouse>(res, "createWarehouse"),

      invalidatesTags: [{ type: "Warehouse", id: "LIST" }],
    }),

    updateWarehouse: builder.mutation<
      Warehouse,
      { id: string; input: UpdateWarehouseInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateWarehouse($id: String!, $input: UpdateWarehouseInput!) {
              updateWarehouse(id: $id, input: $input) {
                ${WAREHOUSE_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<Warehouse>(res, "updateWarehouse"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Warehouse", id },
        { type: "Warehouse", id: "LIST" },
      ],
    }),

    deleteWarehouse: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteWarehouse($id: String!) {
              deleteWarehouse(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<boolean>(res, "deleteWarehouse"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Warehouse", id },
        { type: "Warehouse", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetWarehousesQuery,
  useLazyGetWarehousesQuery,
  useGetWarehouseQuery,
  useLazyGetWarehouseQuery,
  useCreateWarehouseMutation,
  useUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
} = warehouseApi;
